import * as React from "react";
import { clsx } from "clsx";
import { ArrowLeft, X } from "lucide-react";
import { GridSidebarSnapshotResult } from "@/modules/snapshots/grid/sidebar/snapshortResult";
import { BusinessReportOutcome, BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";
import { GridSidebarSourceViewer } from "@/modules/snapshots/grid/sidebar/sourceViewer";
import { GridSidebarTab, GridSidebarTabs } from "@/modules/snapshots/grid/sidebar/tabs";
import { GridSidebarComparison } from "@/modules/snapshots/grid/sidebar/comparison";
import { GridSidebarSearch } from "@/modules/snapshots/grid/sidebar/search";
import { GridSidebarColumnAnalysis } from "@/modules/snapshots/grid/sidebar/columnAnalysis";
import { GridSidebarReviewComparison } from "@/modules/snapshots/grid/sidebar/reviewComparison";
import { useGetSnapshotCollectionsQuery } from "@/modules/snapshots/snapshotCollection.api";

interface SidebarProps {
  isAnimating: boolean;
  onClose: () => void;
  businessReportOutcome: BusinessReportOutcome;
  result?: BusinessReportOutcomeResult;
  initialTab?: GridSidebarTab;
  snapshotCollectionId: string;
  subcollectionId?: string;
  columnName?: string;
  isCitationView?: boolean;
  contractFamilyProjection?: boolean;
  canEditResult?: boolean;
  onSaveResult?: (value: string) => Promise<void>;
  onRevertResult?: () => Promise<void>;
}

export const GridSidebar: React.FC<SidebarProps> = ({
  result,
  businessReportOutcome,
  isAnimating,
  onClose,
  snapshotCollectionId,
  subcollectionId,
  initialTab = "pdfViewer",
  columnName,
  isCitationView = false,
  contractFamilyProjection = false,
  canEditResult = true,
  onSaveResult,
  onRevertResult,
}) => {
  const [tab, setTab] = React.useState<GridSidebarTab>(initialTab);
  const { data: snapshotCollections = [] } = useGetSnapshotCollectionsQuery();
  const snapshotCollection = snapshotCollections.find(c => c.id === snapshotCollectionId);

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300",
          isAnimating ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-label="Snapshot details"
        className={clsx(
          "h-100x fixed right-0 top-0 z-50 transform bg-white shadow-xl transition-transform duration-300 ease-in-out",
          isAnimating ? "translate-x-0" : "translate-x-full",
          {
            "w-[70%]": !!result,
            "w-[50%]": !result,
          },
        )}
      >
        <div className="flex h-screen flex-col overflow-hidden">
          <div className="flex shrink-0 items-center border-b" style={{ height: 57 }}>
            {!!result && (
              <div className="w-2/5 pt-1">
                <h2 className="mb-2 px-4 py-2 text-lg font-semibold">{result.name}</h2>
              </div>
            )}
            <div className={clsx("flex items-center", result ? "w-3/5" : "w-full")}>
              {tab === "columnAnalysis" && (
                <div className="flex items-center gap-2 px-4 py-2">
                  <button
                    className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    onClick={() => setTab(initialTab)}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <h2 className="text-lg font-semibold">Column Analysis: {columnName}</h2>
                </div>
              )}
              {tab !== "columnAnalysis" && !isCitationView && (
                <GridSidebarTabs
                  tab={tab}
                  setTab={setTab}
                  initialTab={initialTab}
                  showReviewTab={!!result?.review?.verdict}
                />
              )}
              <button
                type="button"
                aria-label="Close details"
                className="ml-auto mr-4 rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
                onClick={onClose}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-row divide-x divide-gray-200">
            {!!result && (
              <div className="flex min-h-0 w-2/5 flex-col overflow-hidden pt-1">
                <GridSidebarSnapshotResult
                  setTab={setTab}
                  result={result}
                  businessReportOutcome={businessReportOutcome}
                  snapshotCollectionId={snapshotCollectionId}
                  isCitationView={isCitationView}
                  canEdit={canEditResult}
                  onSaveResult={onSaveResult}
                  onRevertResult={onRevertResult}
                />
              </div>
            )}
            <div className={clsx("flex min-h-0 flex-col overflow-hidden pr-4", result ? "w-3/5" : "w-full")}>
              {tab === "pdfViewer" && <GridSidebarSourceViewer fsEntry={businessReportOutcome.fs_entry} />}
              {tab === "comparison" && <GridSidebarComparison />}
              {tab === "review" && !!result && (
                <GridSidebarReviewComparison businessReportOutcomeId={businessReportOutcome.id} resultId={result.id} />
              )}
              {tab === "search" && !!snapshotCollection && (
                <GridSidebarSearch selectedCollection={snapshotCollection} />
              )}
              {tab === "columnAnalysis" && !!columnName && (
                <GridSidebarColumnAnalysis
                  key={columnName}
                  businessReportId={businessReportOutcome.business_report_id}
                  snapshotCollectionId={snapshotCollectionId}
                  subcollectionId={subcollectionId}
                  fieldName={columnName}
                  contractFamilyProjection={contractFamilyProjection}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
